import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MESSAGING_TEMPLATE_TYPE_OPTIONS } from "@constants/messaging-template-type-options";
import { MESSAGING_TEMPLATE_CREATE_METHOD_OPTIONS } from "@constants/messaging-template-create-method-options";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { MessagingTemplateCreateMethodTypes } from "@enums/messaging-template-create-method-types";
import { SelectOption } from "@idw-types/common";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { MessagingTemplateGeneralInfoValidationErrors } from "@idw-types/common/validation/messaging-template/messaging-template-validation-errors";
import { TemplateAvailabilityLevelSection } from "@containers/modals/add-messaging-template-modal/template-availability-level-section/template-availability-level-section";
import { IDWSession } from "@idw-types/data/idw-session";
import { getTemplateAndLanguageFiles } from "@utils/messaging-template/messaging-template-util";
import { FormattedMessagingTemplate } from "@idw-types/data/messaging-template";

type MessagingTemplateMetadataFormProps = {
    session: IDWSession;
    labelWidth: LabelWidthTypes;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    validationErrors: MessagingTemplateGeneralInfoValidationErrors;
    state: FormattedMessagingTemplate;
    requestInProgress: boolean;
    availableMessagingTemplateTypeOptions: Array<SelectOption<MessagingTemplateTypes>>;
    isVericationServiceDetailsPage: boolean;
    serviceRelyingPartyId: string;
    serviceOrganizationId: string;
    setSelectedLanguage: (language: LocaleTypes) => void;
    onModalClose: () => void;
    handleSubmit: (event: FormEvent) => void;
    setState: (state: FormattedMessagingTemplate) => void;
};

export const MessagingTemplateMetadataForm = (props: MessagingTemplateMetadataFormProps) => {
    const {
        labelWidth,
        axnCompanies,
        axnRelyingParties,
        validationErrors,
        state,
        requestInProgress,
        availableMessagingTemplateTypeOptions,
        session,
        isVericationServiceDetailsPage,
        serviceRelyingPartyId,
        serviceOrganizationId,
        setSelectedLanguage,
        onModalClose,
        handleSubmit,
        setState
    } = props;

    const [createMethod, setCreateMethod] = useState(MessagingTemplateCreateMethodTypes.CREATE_FROM_SCRATCH);

    const handleUpdateCreateMethod = (newValue: SelectOption<MessagingTemplateCreateMethodTypes>) => {
        setCreateMethod(newValue.value);
    };

    const handleUpdateName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            name: event.target.value
        });
    };

    const handleUpdateType = (newValue: SelectOption<MessagingTemplateTypes>) => {
        const { content, languageFiles } = getTemplateAndLanguageFiles(newValue.value);

        setState({
            ...state,
            type: newValue.value,
            template: content,
            languageFiles: languageFiles
        });

        setSelectedLanguage(LocaleTypes.ENGLISH);
    };

    const selectedCreateMethod: SelectOption<MessagingTemplateCreateMethodTypes> = useMemo(() => {
        return MESSAGING_TEMPLATE_CREATE_METHOD_OPTIONS.find(option => option.value === createMethod);
    }, [MESSAGING_TEMPLATE_CREATE_METHOD_OPTIONS, createMethod]);


    const selectedMessagingTemplateTypeOption: SelectOption = useMemo(() => {
        return MESSAGING_TEMPLATE_TYPE_OPTIONS.find(type => type.value === state.type);
    }, [state.type]);

    const resource = resources.modals.addMessagingTemplate;
   
    return (
        <Form onSubmit={handleSubmit}>
            <Modal.Header id={"add-messaging-template-title"} closeButton>
                <Modal.Title>{resource.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <SelectFormGroup
                    controlId={`messaging-template-create-method-select`}
                    label={resource.inputs.iWantTo.label}
                    ariaLabel={resource.inputs.iWantTo.label}
                    automationId={`messaging-template-create-method-select`}
                    onChange={handleUpdateCreateMethod}
                    options={MESSAGING_TEMPLATE_CREATE_METHOD_OPTIONS}
                    selectedOption={selectedCreateMethod}
                    disabled={requestInProgress}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <hr />

                <TemplateAvailabilityLevelSection
                    session={session}
                    isDisabled={requestInProgress}
                    labelWidth={labelWidth}
                    axnCompanies={axnCompanies}
                    isVericationServiceDetailsPage={isVericationServiceDetailsPage}
                    state={state}
                    serviceOrganizationId={serviceOrganizationId}
                    serviceRelyingPartyId={serviceRelyingPartyId}
                    setState={setState}
                    axnRelyingParties={axnRelyingParties}
                    validationErrors={validationErrors}
                />

                <hr />
                <InputFormGroup
                    controlId="messaging-template-name-input"
                    automationId="messaging-template-name-input"
                    label={resource.inputs.name.label}
                    ariaLabel={resource.inputs.name.label}
                    placeholder={resource.inputs.name.placeholder}
                    value={state.name}
                    onChange={handleUpdateName}
                    disabled={requestInProgress}
                    isRequired={true}
                    labelWidth={labelWidth}
                />
                {validationErrors.nameError}
                <SelectFormGroup
                    controlId={`messaging-template-type-select`}
                    label={resource.inputs.type.label}
                    ariaLabel={resource.inputs.type.label}
                    automationId={`messaging-template-type-select`}
                    onChange={handleUpdateType}
                    options={availableMessagingTemplateTypeOptions}
                    selectedOption={selectedMessagingTemplateTypeOption}
                    disabled={requestInProgress}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button id="add-messaging-template-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                <ButtonWithSpinner
                    id="add-messaging-template-next-to-content-button"
                    disabled={requestInProgress || validationErrors.count > 0}
                    hideSpinner={validationErrors.count > 0}
                    variant="primary"
                    type={ButtonTypes.SUBMIT}
                    label={resource.buttons.nextToContent.label}
                />
            </Modal.Footer>
        </Form>
    );
};